import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import {
  getProducerLists,
  deleteList,
  updateListStatus,
  getListFields,
  exportSubmissionsCSV,
  type InterestList,
} from "@/lib/api-interest-lists";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus,
  Play,
  MoreVertical,
  Copy,
  Pencil,
  Download,
  EyeOff,
  Eye,
  Trash2,
  ClipboardList,
  Search,
  CalendarDays,
  Filter,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  panelPage,
  panelTitle,
  panelSurface,
  panelEyebrow,
  panelPill,
  panelRow,
  panelControl,
} from "@/lib/panel-ui";
import { PanelEmpty } from "@/components/producer/event-lists/panel-list";
import { EmptyInterestList } from "@/components/ui/empty-interest-list";
import { InterestListHowItWorksModal } from "@/components/producer/InterestListHowItWorksModal";

export default function ProducerInterestLists() {
  const { t, i18n } = useTranslation("producerMessages");
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<InterestList | null>(null);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft" | "closed">("all");
  const [dateFilter, setDateFilter] = useState<"all" | "30d">("30d");

  const statusConfig: Record<string, { label: string; dotClass: string; badgeClass: string }> = {
    published: {
      label: t("interestLists.statusLabels.published"),
      dotClass: "bg-emerald-500",
      badgeClass: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
    },
    draft: {
      label: t("interestLists.statusLabels.draft"),
      dotClass: "bg-muted-foreground/60",
      badgeClass: "bg-secondary/80 text-muted-foreground border-border/60",
    },
    closed: {
      label: t("interestLists.statusLabels.closed"),
      dotClass: "bg-destructive",
      badgeClass: "bg-destructive/10 text-destructive border-destructive/20",
    },
  };

  const { data: lists, isLoading } = useQuery({
    queryKey: ["interest-lists", user?.id],
    queryFn: () => getProducerLists(user!.id),
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteList(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["interest-lists"] });
      toast({ title: t("interestLists.toasts.listDeleted") });
      setDeleteTarget(null);
    },
    onError: () => toast({ title: t("interestLists.toasts.deleteError"), variant: "destructive" }),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => updateListStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["interest-lists"] });
      toast({ title: t("interestLists.toasts.statusUpdated") });
    },
  });

  const copyLink = (slug: string) => {
    const url = `${window.location.origin}/lista/${slug}`;
    navigator.clipboard.writeText(url);
    toast({ title: t("interestLists.toasts.linkCopied") });
  };

  const handleExport = async (list: InterestList) => {
    try {
      const fields = await getListFields(list.id);
      const csv = await exportSubmissionsCSV(list.id, fields);
      if (!csv) {
        toast({ title: t("interestLists.toasts.noSubscribersToExport") });
        return;
      }
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fileSuffix = i18n.language?.startsWith("en") ? "subscribers" : "inscritos";
      a.download = `${list.name.replace(/\s+/g, "-").toLowerCase()}-${fileSuffix}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: t("interestLists.toasts.csvExported") });
    } catch {
      toast({ title: t("interestLists.toasts.exportError"), variant: "destructive" });
    }
  };

  const filteredLists = useMemo(() => {
    if (!lists) return [];

    const now = Date.now();
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

    return lists.filter((list) => {
      const matchesSearch =
        searchTerm.trim().length === 0 ||
        list.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        list.slug.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === "all" || list.status === statusFilter;

      const createdAt = new Date(list.created_at).getTime();
      const matchesDate = dateFilter === "all" || createdAt >= thirtyDaysAgo;

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [lists, searchTerm, statusFilter, dateFilter]);

  const metrics = useMemo(() => {
    const source = filteredLists;
    const activeLists = source.filter((list) => list.status === "published").length;
    const totalSubscribers = source.reduce((acc, list) => acc + (list.submissions_count || 0), 0);

    let conversionBase = 0;
    let conversionTotal = 0;
    source.forEach((list) => {
      const max = list.max_submissions || 0;
      if (max > 0) {
        conversionBase += 1;
        conversionTotal += ((list.submissions_count || 0) / max) * 100;
      }
    });

    const avgConversion = conversionBase > 0 ? conversionTotal / conversionBase : 0;

    return {
      activeLists,
      totalSubscribers,
      avgConversion,
    };
  }, [filteredLists]);

  const statusFilterOptions = [
    { key: "all", label: t("interestLists.filter.all") },
    { key: "published", label: t("interestLists.filter.published") },
    { key: "draft", label: t("interestLists.filter.draft") },
    { key: "closed", label: t("interestLists.filter.closed") },
  ];

  return (
    <div className={panelPage}>
      {/* Header Solto & Ações */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={panelTitle}>{t("interestLists.header.title")}</h1>
          <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
            {t("interestLists.header.subtitle")}
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => setHowItWorksOpen(true)}
          className={cn(
            panelPill,
            "min-h-[44px] sm:min-h-[38px] rounded-full border-border/80 bg-background text-xs font-medium px-4 gap-2 hover:bg-secondary/50 transition-colors self-start sm:self-auto"
          )}
        >
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Play className="ml-0.5 h-2.5 w-2.5 fill-current" />
          </div>
          <span>Ver como funciona</span>
        </Button>
      </header>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className={cn(panelSurface, "p-5 sm:p-6 shadow-none")}>
          <p className={panelEyebrow}>{t("interestLists.metrics.activeLists")}</p>
          <p className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums text-foreground">
            {metrics.activeLists}
          </p>
        </div>

        <div className={cn(panelSurface, "p-5 sm:p-6 shadow-none")}>
          <p className={panelEyebrow}>{t("interestLists.metrics.totalSubscribers")}</p>
          <p className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums text-foreground">
            {metrics.totalSubscribers.toLocaleString("pt-BR")}
          </p>
        </div>

        <div className={cn(panelSurface, "p-5 sm:p-6 shadow-none")}>
          <p className={panelEyebrow}>{t("interestLists.metrics.avgConversion")}</p>
          <p className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums text-foreground">
            {metrics.avgConversion.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Toolbar de Pesquisa e Filtros */}
      <div
        className={cn(
          panelSurface,
          "rounded-[26px] sm:rounded-full p-2 sm:p-2.5 shadow-none flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2.5"
        )}
      >
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("interestLists.searchPlaceholder")}
            className="mt-0 min-h-[44px] sm:min-h-[38px] h-11 sm:h-9 rounded-full border border-border/60 bg-background/60 pl-10 pr-4 text-xs focus:bg-background placeholder:text-muted-foreground/60 shadow-none transition-colors"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          <Select
            value={statusFilter}
            onValueChange={(val) => setStatusFilter(val as typeof statusFilter)}
          >
            <SelectTrigger
              className={cn(
                panelControl,
                "mt-0 min-h-[44px] sm:min-h-[38px] h-11 sm:h-9 w-full sm:w-44 rounded-full border border-border/60 bg-background/60 text-xs shadow-none"
              )}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-2xl">
              {statusFilterOptions.map((option) => (
                <SelectItem key={option.key} value={option.key} className="rounded-xl text-xs">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDateFilter(dateFilter === "30d" ? "all" : "30d")}
            className={cn(
              panelPill,
              "min-h-[44px] sm:min-h-[38px] rounded-full px-3.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 gap-1.5 shrink-0"
            )}
          >
            <CalendarDays className="h-3.5 w-3.5" />
            <span>
              {dateFilter === "30d"
                ? t("interestLists.filter.last30days")
                : t("interestLists.filter.allPeriod")}
            </span>
          </Button>

          {(searchTerm || statusFilter !== "all" || dateFilter !== "30d") && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setDateFilter("30d");
              }}
              className={cn(
                panelPill,
                "min-h-[44px] sm:min-h-[38px] rounded-full px-3 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 gap-1 shrink-0"
              )}
            >
              <Filter className="h-3.5 w-3.5" />
              <span>{t("interestLists.filter.clear")}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Lista Principal */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className={cn(panelRow, "p-4 sm:p-5 space-y-3")}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3.5">
                  <Skeleton className="h-10 w-10 rounded-2xl" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-36 rounded-lg" />
                    <Skeleton className="h-3 w-24 rounded-lg" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-9 w-20 rounded-full" />
                  <Skeleton className="h-9 w-9 rounded-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : !lists?.length ? (
        <div className={cn(panelSurface, "rounded-[28px] border-border/60 bg-card/60 shadow-none")}>
          <EmptyInterestList
            onCreateClick={() => navigate("/produtor/lista-de-interesse/nova")}
            onHowItWorksClick={() => setHowItWorksOpen(true)}
            title={t("interestLists.emptyState.noLists")}
            subtitle={t("interestLists.emptyState.noListsDesc")}
            buttonText={t("interestLists.emptyState.createFirst")}
          />
        </div>
      ) : !filteredLists.length ? (
        <PanelEmpty
          title={t("interestLists.emptyState.noResults")}
          hint={t("interestLists.emptyState.noResultsDesc")}
        />
      ) : (
        <div className="space-y-3">
          {filteredLists.map((list) => {
            const sc = statusConfig[list.status] || statusConfig.draft;
            const conversion =
              list.max_submissions && list.max_submissions > 0
                ? Math.min(100, ((list.submissions_count || 0) / list.max_submissions) * 100)
                : null;

            return (
              <div
                key={list.id}
                className={cn(
                  panelRow,
                  "p-4 sm:p-5 hover:border-border/90 transition-colors flex flex-col justify-between gap-4"
                )}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Left Info */}
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="h-10 w-10 rounded-2xl bg-secondary/70 text-foreground flex items-center justify-center shrink-0">
                      <ClipboardList className="h-5 w-5" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-sm sm:text-base font-semibold text-foreground truncate">
                          {list.name}
                        </h3>
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border",
                            sc.badgeClass
                          )}
                        >
                          <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", sc.dotClass)} />
                          {sc.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground/80 truncate font-mono mt-0.5">
                        /{list.slug}
                      </p>
                    </div>
                  </div>

                  {/* Metrics & Actions */}
                  <div className="flex flex-wrap sm:flex-nowrap items-center justify-between lg:justify-end gap-4 sm:gap-6 border-t lg:border-t-0 pt-3 lg:pt-0 border-border/40">
                    <div className="grid grid-cols-3 gap-3 sm:flex sm:items-center sm:gap-6 text-left">
                      <div>
                        <span className="font-display font-semibold text-foreground tabular-nums block text-sm">
                          {(list.submissions_count || 0).toLocaleString("pt-BR")}
                        </span>
                        <span className="text-[11px] text-muted-foreground/80">
                          {t("interestLists.card.subscribers")}
                        </span>
                      </div>

                      <div>
                        <span className="font-display font-semibold text-foreground tabular-nums block text-sm">
                          {conversion === null ? "--" : `${conversion.toFixed(0)}%`}
                        </span>
                        <span className="text-[11px] text-muted-foreground/80">
                          {t("interestLists.card.conversion")}
                        </span>
                      </div>

                      <div>
                        <span className="font-medium text-foreground tabular-nums block text-sm">
                          {new Date(list.created_at).toLocaleDateString("pt-BR", {
                            timeZone: "America/Sao_Paulo",
                          })}
                        </span>
                        <span className="text-[11px] text-muted-foreground/80">
                          {t("interestLists.card.created")}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0 ml-auto lg:ml-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/produtor/lista-de-interesse/${list.id}/editar`)}
                        className={cn(
                          panelPill,
                          "min-h-[44px] sm:min-h-[34px] rounded-full px-4 text-xs font-medium border-border/80 bg-background hover:bg-secondary/60 text-foreground transition-colors gap-1.5"
                        )}
                      >
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{t("interestLists.card.edit")}</span>
                      </Button>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              panelPill,
                              "min-h-[44px] min-w-[44px] sm:min-h-[34px] sm:min-w-[34px] rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                            )}
                            aria-label={t("interestLists.card.moreActions") || "Mais ações"}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="rounded-2xl p-1.5 min-w-[180px]"
                        >
                          <DropdownMenuItem
                            onClick={() => copyLink(list.slug)}
                            className="rounded-xl text-xs py-2 cursor-pointer"
                          >
                            <Copy className="mr-2 h-3.5 w-3.5" />{" "}
                            {t("interestLists.card.copyPublicLink")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleExport(list)}
                            className="rounded-xl text-xs py-2 cursor-pointer"
                          >
                            <Download className="mr-2 h-3.5 w-3.5" />{" "}
                            {t("interestLists.card.exportCSV")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {list.status === "published" ? (
                            <DropdownMenuItem
                              onClick={() =>
                                statusMut.mutate({ id: list.id, status: "closed" })
                              }
                              className="rounded-xl text-xs py-2 cursor-pointer"
                            >
                              <EyeOff className="mr-2 h-3.5 w-3.5" />{" "}
                              {t("interestLists.card.unpublish")}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() =>
                                statusMut.mutate({ id: list.id, status: "published" })
                              }
                              className="rounded-xl text-xs py-2 cursor-pointer"
                            >
                              <Eye className="mr-2 h-3.5 w-3.5" />{" "}
                              {t("interestLists.card.publish")}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="rounded-xl text-destructive focus:text-destructive focus:bg-destructive/10 text-xs py-2 cursor-pointer"
                            onClick={() => setDeleteTarget(list)}
                          >
                            <Trash2 className="mr-2 h-3.5 w-3.5" />{" "}
                            {t("interestLists.card.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>

                {/* Progress bar if conversion max set */}
                {conversion !== null && (
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary/60 mt-1">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-300"
                      style={{ width: `${conversion}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Diálogo de Confirmação de Exclusão */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-[28px] border border-border/60 bg-card p-6 sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-base font-semibold text-destructive">
              {t("interestLists.deleteDialog.title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-muted-foreground leading-relaxed">
              {t("interestLists.deleteDialog.description", { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-2 flex-col sm:flex-row">
            <AlertDialogCancel
              className={cn(
                panelPill,
                "min-h-[44px] sm:min-h-[38px] rounded-full border border-border/70 text-xs font-medium"
              )}
            >
              {t("interestLists.deleteDialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                panelPill,
                "min-h-[44px] sm:min-h-[38px] rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs font-medium"
              )}
              onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
            >
              {t("interestLists.deleteDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Como Funciona com Animação de 10s */}
      <InterestListHowItWorksModal
        open={howItWorksOpen}
        onOpenChange={setHowItWorksOpen}
        onCreateList={() => navigate("/produtor/lista-de-interesse/nova")}
      />
    </div>
  );
}
